// leak search from measurements: measured source inflows + hydrant pressures vs. simulated leak positions
import { buildNet, withElevation, simulate, isSource, profOf } from './aquaNet';
import { isRot, armState } from './fitSymbols';
import { isHouse } from './netCheck';
import { resolvePt } from './model';

const num = (v) => (v === '' || v == null || !Number.isFinite(+v) ? null : +v);
const M = 111320;
const xy = (n) => [n.lat * M, n.lon * M * Math.cos((n.lat * Math.PI) / 180)];
const d2 = (a, b) => (a[0] - b[0]) ** 2 + (a[1] - b[1]) ** 2;
const noLeaks = (p) => ({ ...p, fittings: (p.fittings || []).filter((f) => f.type !== 'leak') });

export const srcFits = (project) => (project.fittings || []).filter(isSource);

// measuring time: demand factor of the day profile at that hour (or entered), on top of the general factor
export const weFac = (cfg, day) => (day === 'we' ? num(cfg.weF) ?? 1 : 1);
export const hourFac = (cfg, day, h) => profOf(cfg, day)[h] * weFac(cfg, day);
export const measCfg = (cfg) => {
  const ls = cfg.ls || {};
  if (ls.hour == null || ls.hour === '') return ls.day === 'we' ? { ...cfg, demandF: (num(cfg.demandF) ?? 1) * weFac(cfg, 'we') } : cfg;
  const f = num(ls.fac) ?? hourFac(cfg, ls.day, +ls.hour);
  return { ...cfg, demandF: f * (num(cfg.demandF) ?? 1) };
};

const leakCands = (base, net) => Object.values(base.nodes).filter((n) => !n.twin && !net.srcIds.includes(n.id) && Number.isFinite(n.bar) && !isHouse(net.byId[n.fid]) && net.byId[n.fid]?.type !== 'hydrant').map((n) => ({ id: n.id, p: xy(n) }));

// which hydrants to measure: greedy choice that best separates leak positions (and a leak from no leak), ±0.02 bar loggers
export async function suggestPoints(project, cfg, k, qTest, onStep, stop) {
  const p0 = noLeaks(project), net = buildNet(p0, { valves: cfg.scen?.valves });
  if (!net.srcIds.length) return { error: 'noSource' };
  await withElevation(net);
  const run = (extra) => simulate(p0, cfg, { net, noHydRes: true, extra });
  const base = await run(null);
  if (base.error) return { error: base.error };
  const H = (project.fittings || []).filter((f) => f.type === 'hydrant' && Number.isFinite(base.nodes[f.id]?.bar));
  if (!H.length) return { error: 'noHyd' };
  const C = spread(leakCands(base, net), 16), D = [];
  for (const c of C) {
    if (stop?.()) return { error: 'cancel' };
    const r = await run({ [c.id]: qTest });
    if (r.error === 'cancel') return { error: 'cancel' };
    if (!r.error) D.push(H.map((h) => (base.nodes[h.id].bar - (r.nodes[h.id]?.bar ?? base.nodes[h.id].bar))));
    onStep?.(D.length);
  }
  const s2 = 0.02 ** 2, n = D.length;
  const score = (S) => { let v = 0; for (let i = 0; i < n; i++) { v += Math.min(1, S.reduce((a, j) => a + D[i][j] ** 2, 0) / s2); for (let o = i + 1; o < n; o++) v += Math.min(1, S.reduce((a, j) => a + (D[i][j] - D[o][j]) ** 2, 0) / s2); } return v; };
  const sens = H.map((_, j) => D.reduce((a, r) => a + r[j], 0) / Math.max(1, n)), chosen = [];
  while (chosen.length < Math.min(k, H.length)) {
    let bj = -1, bv = -1;
    H.forEach((_, j) => { if (chosen.includes(j)) return; const v = score([...chosen, j]) + sens[j] * 1e-3; if (v > bv) { bv = v; bj = j; } });
    chosen.push(bj);
  }
  const full = n + (n * (n - 1)) / 2;
  return { q: qTest, runs: n, cover: full ? score(chosen) / full : 0, pts: chosen.map((j) => ({ id: H[j].id, name: H[j].name, lat: H[j].lat, lon: H[j].lon, sens: sens[j], max: Math.max(...D.map((r) => r[j])) })) };
}

// measured values from the current simulation (with the placed leaks) – for training and checking the search
export async function measFromSim(project, cfg, hydIds) {
  const r = await simulate(project, cfg, { noHydRes: true });
  if (r.error) return { error: r.error };
  const src = {}, hyd = {};
  srcFits(project).forEach((f) => { const n = r.nodes[f.id]; if (n) src[f.id] = +(-n.demand).toFixed(2); });
  hydIds.forEach((id) => { const b = r.nodes[id]?.bar; if (Number.isFinite(b)) hyd[id] = +b.toFixed(2); });
  return { src, hyd };
}

// farthest point sampling: k candidates spread over the network
function spread(list, k) {
  if (list.length <= k) return [...list];
  const out = [list[0]], dmin = list.map((n) => d2(n.p, list[0].p));
  while (out.length < k) { let j = 0; dmin.forEach((v, i) => { if (v > dmin[j]) j = i; }); out.push(list[j]); list.forEach((n, i) => { dmin[i] = Math.min(dmin[i], d2(n.p, list[j].p)); }); }
  return out;
}

export async function leakSearch(project, cfg, meas, onStep, stop) {
  const p0 = noLeaks(project), net = buildNet(p0, { valves: cfg.scen?.valves });
  if (!net.srcIds.length) return { error: 'noSource' };
  await withElevation(net);
  const opt = { net, noHydRes: true }, run = (extra) => simulate(p0, cfg, { ...opt, extra });
  const base = await run(null);
  if (base.error) return { error: base.error };
  const S = Object.entries(meas.src || {}).map(([id, v]) => [id, num(v)]).filter(([id, v]) => v != null && base.nodes[id]);
  const H = Object.entries(meas.hyd || {}).map(([id, v]) => [id, num(v)]).filter(([id, v]) => v != null && Number.isFinite(base.nodes[id]?.bar));
  if (!S.length) return { error: 'noMeas' };
  const qLeak = S.reduce((s, [id, v]) => s + v - -base.nodes[id].demand, 0);
  const tol = Math.max(0.05, 0.01 * Math.abs(S.reduce((s, [, v]) => s + v, 0)));
  const out = { qLeak, base: -S.reduce((s, [id]) => s + base.nodes[id].demand, 0), meas: S.reduce((s, [, v]) => s + v, 0), nH: H.length, nS: S.length };
  if (qLeak <= tol) return { ...out, qLeak: Math.max(0, qLeak), none: true };
  if (!H.length) return { ...out, discr: false, links: [], top: [] };
  // error of a run against the measurements (inflow ±2 %, pressure logger ±0.02 bar)
  const err = (r) => S.reduce((s, [id, v]) => s + ((-r.nodes[id].demand - v) / Math.max(0.05, 0.02 * Math.abs(v))) ** 2, 0) + H.reduce((s, [id, v]) => { const b = r.nodes[id]?.bar; return s + (Number.isFinite(b) ? ((b - v) / 0.02) ** 2 : 400); }, 0);
  const cand = leakCands(base, net);
  const ev = new Map(), discr = H.length > 0;
  const evalAll = async (list) => {
    for (const c of list) {
      if (stop?.()) return false;
      if (ev.has(c.id)) continue;
      const r = await run({ [c.id]: qLeak });
      if (r.error === 'cancel') return false;
      ev.set(c.id, { ...c, e: r.error ? Infinity : err(r) });
      onStep?.(ev.size);
    }
    return true;
  };
  if (discr) {
    if (!(await evalAll(spread(cand, 24)))) return { error: 'cancel' };
    const best = [...ev.values()].sort((a, b) => a.e - b.e).slice(0, 3);
    const near = best.flatMap((b) => cand.filter((c) => !ev.has(c.id)).sort((x, y) => d2(x.p, b.p) - d2(y.p, b.p)).slice(0, 8));
    if (!(await evalAll(near))) return { error: 'cancel' };
  }
  const E = [...ev.values()], e0 = Math.min(...E.map((c) => c.e));
  E.forEach((c) => { c.w = Number.isFinite(c.e) ? Math.exp(-(c.e - e0) / 2) : 0; });
  // pipe pieces: likelihood from the nearest evaluated positions (inverse distance)
  const pn = Object.fromEntries((project.pipes || []).map((x) => [x.id, x]));
  const idw = (pm) => { const nb = E.map((c) => [d2(c.p, pm) + 25, c.w]).sort((a, b) => a[0] - b[0]).slice(0, 3); return nb.reduce((a, [dd, w]) => a + w / dd, 0) / nb.reduce((a, [dd]) => a + 1 / dd, 0); };
  const hsEnd = (l) => isHouse(net.byId[net.nodes[l.a]?.fid]) || isHouse(net.byId[net.nodes[l.b]?.fid]);
  const links = base.links.filter((l) => l.kind === 'pipe' && !l.dead && pn[l.pipeId]?.type !== 'house' && !hsEnd(l)).map((l) => {
    const a = l.coords[0], b = l.coords[l.coords.length - 1], m = l.coords[Math.floor(l.coords.length / 2)];
    // likelihood density sampled along the piece (start, quarter, middle, three quarters, end)
    const smp = [0, 0.25, 0.5, 0.75, 1].map((u) => xy({ lat: a[0] + (b[0] - a[0]) * u, lon: a[1] + (b[1] - a[1]) * u }));
    const s = E.length ? smp.reduce((acc, pm) => acc + idw(pm), 0) / smp.length : 1;
    return { id: l.id, pipeId: l.pipeId, coords: l.coords, len: l.len, d: l.d, s, mid: m };
  });
  const tot = links.reduce((a, l) => a + l.s, 0) || 1, ss = links.map((l) => l.s).sort((a, b) => a - b), mx = ss[ss.length - 1] || 1, med = ss[Math.floor(ss.length / 2)] || 0;
  links.forEach((l) => { l.prob = l.s / tot; l.rel = mx > med ? Math.max(0, (l.s - med) / (mx - med)) : 1; });
  // ranking per drawn pipe (pieces of one pipe merged: best piece, total length, summed probability)
  const byPipe = {};
  links.forEach((l) => { const g = byPipe[l.pipeId]; if (!g) byPipe[l.pipeId] = { ...l }; else { g.prob += l.prob; g.len += l.len; if (l.s > g.s) Object.assign(g, { id: l.id, s: l.s, mid: l.mid, d: l.d, rel: l.rel }); } });
  const flat = discr && mx < med * 1.5;
  return { ...out, discr, flat, runs: ev.size, links, top: Object.values(byPipe).sort((a, b) => b.prob - a.prob).slice(0, 10), at: Date.now() };
}

// area to shut off around a leak on its host pipe (next valves / valve arms) with the households losing supply
export function leakArea(project, leak) {
  const F = Object.fromEntries((project.fittings || []).map((f) => [f.id, f])), host = (project.pipes || []).find((p) => p.id === leak.hostPipe);
  const res = { edges: [], valves: [], houses: [], hydrants: [], len: 0 };
  if (!host) return res;
  const key = (pt) => (pt?.fid ? `f:${pt.fid}` : Array.isArray(pt) ? `c:${pt[0].toFixed(6)},${pt[1].toFixed(6)}` : null);
  const adj = {}, pos = {};
  (project.pipes || []).filter((p) => p.plan !== 'removed' && p.status !== 'abandoned').forEach((p) => {
    const ks = (p.points || []).map((pt) => { const k = key(pt), ll = resolvePt(pt, project.fittings || []); if (k && ll) pos[k] = ll; return k && ll ? k : null; });
    for (let i = 1; i < ks.length; i++) if (ks[i] && ks[i - 1] && ks[i] !== ks[i - 1]) { (adj[ks[i - 1]] ||= []).push({ to: ks[i], p, out: `${p.id}>`, inn: `${p.id}<` }); (adj[ks[i]] ||= []).push({ to: ks[i - 1], p, out: `${p.id}<`, inn: `${p.id}>` }); }
  });
  const fOf = (k) => (k.startsWith('f:') ? F[k.slice(2)] : null), bnd = new Set(), seen = new Set(), eSeen = new Set(), q = [];
  const shut = (f, arm) => f && (f.type === 'valve' || (isRot(f) && armState(f, arm) !== 'none'));
  const enter = (k, arm) => { const f = fOf(k); if (shut(f, arm)) { bnd.add(f.id); return; } if (!seen.has(k)) { seen.add(k); q.push(k); } };
  const ks = host.points.map(key), P = host.points.map((pt) => resolvePt(pt, project.fittings || []));
  let j = 1, bd = Infinity;
  for (let i = 1; i < P.length; i++) { if (!P[i] || !P[i - 1]) continue; const m = [(P[i][0] + P[i - 1][0]) / 2, (P[i][1] + P[i - 1][1]) / 2], dd = d2(xy({ lat: m[0], lon: m[1] }), xy(leak)); if (dd < bd) { bd = dd; j = i; } }
  if (ks[j - 1]) enter(ks[j - 1], `${host.id}>`);
  if (ks[j]) enter(ks[j], `${host.id}<`);
  if (P[j - 1] && P[j]) { res.edges.push([P[j - 1], P[j]]); eSeen.add([ks[j - 1], ks[j]].sort().join('|') + '|' + host.id); }
  while (q.length) {
    const u = q.shift(), fu = fOf(u);
    (adj[u] || []).forEach((e) => {
      if (fu && isRot(fu) && armState(fu, e.out) !== 'none') { bnd.add(fu.id); return; }
      const id = [u, e.to].sort().join('|') + '|' + e.p.id;
      if (eSeen.has(id)) return;
      eSeen.add(id); res.edges.push([pos[u], pos[e.to]]);
      enter(e.to, e.inn);
    });
  }
  const inside = [...seen].map(fOf).filter(Boolean);
  res.valves = [...bnd]; res.houses = inside.filter(isHouse).map((f) => f.id); res.hydrants = inside.filter((f) => f.type === 'hydrant').map((f) => f.id);
  res.len = res.edges.reduce((s, [a, b]) => s + Math.sqrt(d2(xy({ lat: a[0], lon: a[1] }), xy({ lat: b[0], lon: b[1] }))), 0);
  return res;
}
